/**
 * @fileoverview Reassign / request-reassignment dialog for a My Page target.
 * @description One dialog for the two ways a target changes hands. A Super Admin, Sales Head or
 * Sales Manager reassigns it at once (Reassign Target, which needs a recent MFA sign-in and sends the
 * version the details showed as `If-Match`); a Sales Executive asks for it instead (Request Target
 * Reassignment, with a required reason, which a reviewer must approve). Both offer only the people
 * the backend lists as eligible for this target. The backend rechecks everything, so a refusal shows
 * as the dialog's error. The parent mounts it only while it is open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useEligibleMyPageReassignees } from '@/features/sales/hooks/useEligibleMyPageReassignees'
import { useReassignMyPageTarget } from '@/features/sales/hooks/useReassignMyPageTarget'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { useRequestMyPageReassignment } from '@/features/sales/hooks/useRequestMyPageReassignment'
import { isStaleMyPageError } from '@/features/sales/lib/my-page-stale.utils'
import {
  myPageReassignFormSchema,
  myPageRequestReassignmentFormSchema,
} from '@/features/sales/schemas/my-page-reassign-form.schema'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import MyPageReasonField from './MyPageReasonField'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { MyPageReassignFormValues } from '@/features/sales/schemas/my-page-reassign-form.schema'
import type { MyPageTargetDetails } from '@/features/sales/types/my-page.types'
import type { FormEvent } from 'react'

interface MyPageReassignDialogProps {
  target: MyPageTargetDetails
  mode: 'reassign' | 'request'
  onClose: () => void
}

/**
 * @component
 * @param {MyPageReassignDialogProps} props - The target, which way it changes hands, and the close
 * handler.
 * @param {MyPageTargetDetails} props.target - The target as the details last showed it.
 * @param {'reassign' | 'request'} props.mode - `reassign` moves it now; `request` asks a reviewer.
 * @param {() => void} props.onClose - Called when the dialog is dismissed or the change is saved.
 */
function MyPageReassignDialog({ target, mode, onClose }: MyPageReassignDialogProps) {
  const { t } = useTranslation()
  const isRequest = mode === 'request'
  const eligible = useEligibleMyPageReassignees(target.targetId)
  const reassign = useReassignMyPageTarget(onClose)
  const request = useRequestMyPageReassignment(onClose)
  const refreshSales = useRefreshSalesQueries()
  const people = eligible.data ?? []
  const mutation = isRequest ? request : reassign
  const close = useGuardedClose(onClose, mutation.isPending)
  const isStale = !isRequest && reassign.isError && isStaleMyPageError(reassign.error)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MyPageReassignFormValues>({
    resolver: zodResolver(
      isRequest ? myPageRequestReassignmentFormSchema : myPageReassignFormSchema,
    ),
    defaultValues: { assigneeUserId: '', reason: '' },
  })

  /**
   * @description Reassigns the target, or asks for it to be reassigned.
   * @param {MyPageReassignFormValues} values - The form's values.
   */
  const onValid = (values: MyPageReassignFormValues) => {
    if (isRequest) {
      request.mutate({
        targetId: target.targetId,
        input: { assigneeUserId: values.assigneeUserId, reason: values.reason },
      })
      return
    }
    reassign.mutate({
      targetId: target.targetId,
      version: target.version,
      input: {
        assigneeUserId: values.assigneeUserId,
        ...(values.reason ? { reason: values.reason } : {}),
      },
    })
  }

  /**
   * @description Reloads the latest state and returns to the details for review.
   */
  const onRefresh = () => {
    refreshSales()
    onClose()
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="my-page-reassign-dialog"
      title={t(
        isRequest
          ? 'sales.myWork.dialogs.reassign.requestTitle'
          : 'sales.myWork.dialogs.reassign.title',
      )}
      subtitle={target.bankName}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="my-page-reassign-form"
      >
        <p className="text-[13.5px] text-ink" data-testid="my-page-reassign-current">
          {t('sales.myWork.dialogs.reassign.current', { name: target.assignee.displayName })}
        </p>
        <SelectField
          id="myPageAssignee"
          data-testid="my-page-reassign-select"
          label={t('sales.myWork.dialogs.reassign.assigneeLabel')}
          disabled={!eligible.isSuccess || people.length === 0}
          options={[
            { value: '', label: t('sales.myWork.dialogs.reassign.placeholder') },
            ...people.map((person) => ({
              value: person.userId,
              label: person.employeeCode
                ? `${person.displayName} (${person.employeeCode})`
                : person.displayName,
            })),
          ]}
          error={errors.assigneeUserId?.message ? t(errors.assigneeUserId.message) : undefined}
          {...register('assigneeUserId')}
        />
        {eligible.isSuccess && people.length === 0 && (
          <p className="text-[12px] text-slate" data-testid="my-page-reassign-none">
            {t('sales.myWork.dialogs.reassign.none')}
          </p>
        )}
        {eligible.isError && <SalesRequestError error={eligible.error} />}
        <MyPageReasonField
          id="myPageReassignReason"
          data-testid="my-page-reassign-reason"
          label={t(
            isRequest
              ? 'sales.myWork.dialogs.reasonRequiredLabel'
              : 'sales.myWork.dialogs.reasonOptionalLabel',
          )}
          placeholder={t('sales.myWork.dialogs.reasonPlaceholder')}
          error={errors.reason?.message ? t(errors.reason.message) : undefined}
          {...register('reason')}
        />
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={false} />}
        {mutation.isError && !isStale && <SalesRequestError error={mutation.error} />}
        <DialogActions
          testIdPrefix="my-page-reassign"
          cancelLabel={t('sales.myWork.dialogs.cancel')}
          confirmLabel={t(
            isRequest
              ? 'sales.myWork.dialogs.reassign.requestSubmit'
              : 'sales.myWork.dialogs.reassign.submit',
          )}
          onCancel={close}
          confirmType="submit"
          isLoading={mutation.isPending}
          disabled={!eligible.isSuccess || people.length === 0 || isStale}
        />
      </form>
    </Modal>
  )
}

export default MyPageReassignDialog
